import { useCallback, useEffect, useRef, useState } from 'react';

import { buyGiftListing, fetchShopListings, type ShopGiftListing } from './api';
import type { GiftFilter, GiftSortBy } from './types';

type State = {
  listings: ShopGiftListing[];
  loading: boolean;
  error: string | null;
  buying: string | null; // listing id currently being purchased
};

export function useShopGifts() {
  const [state, setState] = useState<State>({
    listings: [],
    loading: false,
    error: null,
    buying: null,
  });

  // Abort controller for cleanup on unmount / re-fetch
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const listings = await fetchShopListings();
      setState((s) => ({ ...s, listings, loading: false }));
    } catch (err: unknown) {
      if ((err as { name?: string }).name === 'CanceledError') return;
      setState((s) => ({
        ...s,
        loading: false,
        error: 'Failed to load gift listings',
      }));
    }
  }, []);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const buy = useCallback(async (listingId: string): Promise<boolean> => {
    setState((s) => ({ ...s, buying: listingId }));
    try {
      await buyGiftListing(listingId);
      // Remove the purchased listing from local state immediately
      setState((s) => ({
        ...s,
        buying: null,
        listings: s.listings.filter((l) => l.id !== listingId),
      }));
      return true;
    } catch {
      setState((s) => ({ ...s, buying: null }));
      return false;
    }
  }, []);

  // Client-side filter + sort (server already applies window rule)
  const getFiltered = useCallback(
    (search: string, filter: GiftFilter, sortBy: GiftSortBy): ShopGiftListing[] => {
      let result = state.listings;

      if (filter === 'lost') result = result.filter((l) => l.is_lost);

      if (search.trim()) {
        const q = search.toLowerCase();
        result = result.filter((l) => l.name.toLowerCase().includes(q));
      }

      return [...result].sort((a, b) => {
        if (sortBy === 'price_asc') return a.price_ton - b.price_ton;
        if (sortBy === 'price_desc') return b.price_ton - a.price_ton;
        return a.name.localeCompare(b.name);
      });
    },
    [state.listings],
  );

  return {
    loading: state.loading,
    error: state.error,
    buying: state.buying,
    reload: load,
    buy,
    getFiltered,
  };
}
